import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAppLanguage } from '@/hooks/useAppLanguage';
import { articlesApi } from '@/lib/api';
import { ChevronLeft, Home } from 'lucide-react';
import Colors from '@/constants/colors';
import { getTranslation } from '@/constants/translations';
import PageContainer from '@/components/PageContainer';
import ContentSection from '@/components/ContentSection';
import CompactCard from '@/components/CompactCard';
import { LexicalRenderer } from '@/components/LexicalRenderer';
import { extractPlainText } from '@/lib/lexical-parser';
import { DetailActionButtons } from '@/components/DetailActionButtons';
import { ScrollingBackButton } from '@/components/ScrollingBackButton';
import { useNavigationSource } from '@/hooks/useNavigationSource';

export default function ArticleDetail() {
  const { language } = useAppLanguage();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isDirectLink } = useNavigationSource();

  const { data: article, isLoading, error } = useQuery({
    queryKey: ['articles', id, language],
    queryFn: () => id ? articlesApi.getById(id, language) : null,
    enabled: !!id,
  });

  const { data: relatedArticles = [] } = useQuery({
    queryKey: ['articles', 'related', id, language],
    queryFn: () => article?.relatedArticles && article.relatedArticles.length > 0
      ? articlesApi.getByIds(article.relatedArticles, language)
      : Promise.resolve([]),
    enabled: !!article?.relatedArticles?.length,
  });

  if (isLoading) {
    return (
      <PageContainer>
        <ContentSection style={{ padding: '24px', textAlign: 'center' }}>
          <p style={styles.notFound}>Loading article...</p>
        </ContentSection>
      </PageContainer>
    );
  }

  if (error || !article) {
    return (
      <PageContainer>
        <ContentSection style={{ padding: '24px', textAlign: 'center' }}>
          <p style={styles.notFound}>Article not found</p>
        </ContentSection>
      </PageContainer>
    );
  }

  const getTitle = (art: typeof article) =>
    language === 'ru' ? art.title_ru || art.title : art.title;

  const getContent = (art: typeof article) =>
    language === 'ru' ? art.content_ru || art.content : art.content || art.summary;

  const title = getTitle(article);
  const content = getContent(article);

  return (
    <PageContainer>
      <ScrollingBackButton />

      {/* Hero Section */}
      <div style={styles.heroContainer}>
        <img
          src={article.image_url || article.imageUrl}
          alt={title}
          style={styles.heroImage}
          loading="lazy"
          decoding="async"
        />
        <div style={styles.heroOverlay} />
        <div style={styles.heroContent}>
          <div style={styles.heroTopRow}>
            <button
              style={styles.backButton}
              onClick={() => isDirectLink ? navigate('/') : navigate(-1)}
              aria-label={isDirectLink ? 'Go to home' : 'Go back'}
            >
              {isDirectLink ? <Home size={24} color="white" /> : <ChevronLeft size={24} color="white" />}
            </button>
            <DetailActionButtons
              id={article.id}
              title={title}
              shareUrl={`/articles/${article.id}`}
              hideShare={isDirectLink}
            />
          </div>
          <h1 style={styles.heroTitle}>{title}</h1>
        </div>
      </div>

      {/* Category Badge */}
      <ContentSection style={styles.badgeContainer}>
        <span style={styles.badge}>{article.category?.toUpperCase()}</span>
      </ContentSection>

      {/* Content */}
      <ContentSection style={{ padding: '0 24px' }}>
        <LexicalRenderer content={content || ''} />
      </ContentSection>

      {/* Related Articles Section */}
      {relatedArticles.length > 0 && (
        <ContentSection style={styles.relatedSection}>
          <h2 style={styles.relatedTitle}>{getTranslation('relatedArticles', language)}</h2>
          <div style={styles.relatedArticlesContainer}>
            {relatedArticles.map((relatedArticle) => (
              <CompactCard
                key={relatedArticle.id}
                id={relatedArticle.id}
                imageUrl={relatedArticle.image_url || relatedArticle.imageUrl}
                imageAlt={getTitle(relatedArticle)}
                title={getTitle(relatedArticle)}
                description={`${extractPlainText(getContent(relatedArticle) || '').substring(0, 100) || 'No description'}...`}
                promoted={!!relatedArticle.featured}
                onCardClick={() => navigate(`/articles/${relatedArticle.id}`, { state: { fromApp: true } })}
              />
            ))}
          </div>
        </ContentSection>
      )}

      {/* Spacing for bottom nav */}
      <div style={{ height: '40px' }} />
    </PageContainer>
  );
}

const styles = {
  heroContainer: {
    position: 'relative',
    width: '100%',
    height: '300px',
    overflow: 'hidden',
  } as React.CSSProperties,

  heroImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  } as React.CSSProperties,

  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6))',
  } as React.CSSProperties,

  heroContent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '16px 24px',
  } as React.CSSProperties,

  heroTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  } as React.CSSProperties,

  backButton: {
    background: 'rgba(0,0,0,0.4)',
    border: 'none',
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s ease',
  } as React.CSSProperties,

  heroTitle: {
    margin: 0,
    fontSize: '32px',
    fontWeight: '700',
    color: 'white',
    lineHeight: '1.2',
  } as React.CSSProperties,

  badgeContainer: {
    padding: '24px 24px 0',
  } as React.CSSProperties,

  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    backgroundColor: Colors.ice,
    color: Colors.primaryDark,
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase' as const,
  } as React.CSSProperties,

  bodyText: {
    fontSize: '16px',
    color: Colors.text,
    lineHeight: '24px',
    margin: 0,
    marginBottom: '16px',
  } as React.CSSProperties,

  notFound: {
    color: Colors.textSecondary,
  } as React.CSSProperties,

  relatedSection: {
    padding: '40px 24px 0',
    marginTop: '24px',
  } as React.CSSProperties,

  relatedTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: Colors.text,
    margin: 0,
    marginBottom: '16px',
  } as React.CSSProperties,

  relatedArticlesContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  } as React.CSSProperties,
};
